import Placeholder, { PlaceholderOptions } from '@tiptap/extension-placeholder'
import { isNodeEmpty, type Editor } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin } from '@tiptap/pm/state'

export const EMPTY_NODE_CLASS = 'is-empty'
export const EMPTY_EDITOR_CLASS = 'is-editor-empty'

// Per editor: TipTap 2 extension storage belongs to the extension instance, which every editor shares
const placeholderTexts = new WeakMap<Editor, string>()

// Elements whose boundaries become line breaks when markup is flattened to placeholder text
const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV', 'LI', 'BLOCKQUOTE', 'BR'])
// Elements whose content is never shown as text
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE'])

const PLACEHOLDER_BLOCK_TYPES = new Set(['paragraph', 'heading'])

/**
 * The slash placeholder is only for a blank text line. An empty checklist or bullet list has no text, so TipTap
 * reports the editor as empty, but the list itself is the content and must not be covered by the placeholder.
 */
export function showsEditorPlaceholder(doc: ProseMirrorNode): boolean {
  if (doc.childCount !== 1) return false
  const block = doc.firstChild
  if (!block || !PLACEHOLDER_BLOCK_TYPES.has(block.type.name)) return false
  return isNodeEmpty(block)
}

/**
 * Marks an empty editor's text block with `is-editor-empty` and `data-placeholder`; TextEditor's CSS shows the text
 * and decides when (focus rules). The text is kept per editor, so the host can change it without a new editor.
 */
export const EditorPlaceholder = Placeholder.extend<PlaceholderOptions>({
  addProseMirrorPlugins() {
    // The stock plugin walks the document on every update and also marks the empty line under the caret in a
    // non-empty document. Only an empty editor with text to show displays anything, so skip the work otherwise:
    // most editors have content, and readers get no placeholder at all.
    const { editor } = this
    return (this.parent?.() || []).map(plugin => {
      const decorations = plugin.spec.props?.decorations
      if (!decorations) return plugin
      return new Plugin({
        ...plugin.spec,
        props: {
          ...plugin.spec.props,
          decorations(state) {
            return placeholderTexts.get(editor) && showsEditorPlaceholder(state.doc)
              ? decorations.call(this, state)
              : null
          }
        }
      })
    })
  }
}).configure({
  emptyNodeClass: EMPTY_NODE_CLASS,
  emptyEditorClass: EMPTY_EDITOR_CLASS,
  placeholder: ({ editor }: { editor: Editor }) => placeholderTexts.get(editor) || '',
  // Hosts pass no placeholder where the reader cannot edit, as they did for the old overlay
  showOnlyWhenEditable: false
})

/** Sets the text an empty editor shows. */
export function setEditorPlaceholder(editor: Editor, text: string): void {
  if (editor.isDestroyed || (placeholderTexts.get(editor) || '') === text) return
  placeholderTexts.set(editor, text)
  // Redraw decorations from the unchanged state: no transaction, so no plugin state, history or save work.
  // A non-empty document picks the text up on the update that empties it.
  if (showsEditorPlaceholder(editor.state.doc)) editor.view.updateState(editor.state)
}

/**
 * The placeholder as plain text, one line per block. A few i18n placeholders hold markup (a sample heading, a bold
 * lead-in); `content: attr()` can only show text, so tags are dropped and entities decoded by an inert parse.
 */
export function toPlaceholderText(value: string | null | undefined): string {
  if (!value) return ''
  if (!/[<&]/.test(value)) return value
  const body = new DOMParser().parseFromString(value, 'text/html').body
  let text = ''
  const walk = (node: Node): void => {
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent
      } else if (child.nodeType === Node.ELEMENT_NODE && !SKIPPED_TAGS.has((child as Element).tagName)) {
        const isBlock = BLOCK_TAGS.has((child as Element).tagName)
        if (isBlock) text += '\n'
        walk(child)
        if (isBlock) text += '\n'
      }
    })
  }
  walk(body)
  return text
    .split('\n')
    .map(line => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
}
